/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import T from 'components/T'
import { CheckIcon } from '@heroicons/react/24/solid'
import { Offer } from 'types/entities/Offer'
import UiTextarea from 'components/Ui/UiTextarea'
import UiButton, { UiButtonSize, UiButtonStyle, UiButtonType } from 'components/Ui/UiButton'
import UiCard, { UiCardContent, UiCardHeader, UiCardTitle } from "components/Ui/UiCard"
import { useOfferExtraNoteForm } from 'utils/utility'

interface OfferOfferNoteProps {
    offer: Offer
    className?: string
    disabled?: boolean
}

const OfferOfferNote = (props: OfferOfferNoteProps) => {
    const { t } = useTranslation()
    const { register, handleSubmit, errors, isLoading, isNoteChanged, onErrorHandler, onSubmitHandler } =
        useOfferExtraNoteForm({
            offer: props.offer,
            noteField: 'offer_note',
            successMessage: 'Offer note saved',
            errorMessage: 'Offer note not saved',
        })
    const isDisabled = !!props.disabled

    return (
        <UiCard
            className={clsx(
                'OfferOfferNote',
                props.className
            )}
            padding={"sm"}
        >
            <form
                className={clsx('flex flex-col w-full h-full')}
                onSubmit={isDisabled ? (e) => e.preventDefault() : handleSubmit(onSubmitHandler, onErrorHandler)}
            >
                <UiCardHeader>
                    <UiCardTitle className={"text-sm!"}>
                        {t('Offer note')}
                    </UiCardTitle>
                    <UiButton
                        title={`${t('Save')}: ${t('Offer note')}`}
                        type={UiButtonType.Submit}
                        style={UiButtonStyle.GradientGreen}
                        size={UiButtonSize.Small}
                        disabled={isDisabled || !isNoteChanged || isLoading}
                    >
                        <CheckIcon className="w-4 h-4 text-white" />
                    </UiButton>
                </UiCardHeader>

                <UiCardContent>
                    <UiTextarea
                        aria-label={t('Offer note')}
                        rows={3}
                        {...register('offer_note')}
                        error={errors?.offer_note?.message}
                        className={clsx('w-full flex-1 text-xs')}
                        disabled={isDisabled}
                        onBlur={isDisabled ? undefined : handleSubmit(onSubmitHandler, onErrorHandler)}
                    />

                    {errors?.['offer_note']?.message && (
                        <div
                            className={clsx(
                                'text-red-500 mt-1 text-xs flex flex-row items-center shrink-0'
                            )}
                        >
                            <div className={clsx('flex flex-row items-center')}>
                                <ExclamationCircleIcon
                                    className={
                                        'min-w-3.5 min-h-3.5 w-3.5 h-3.5 mr-0.5'
                                    }
                                />
                                <T>{errors?.['offer_note']?.message}</T>
                            </div>
                        </div>
                    )}
                </UiCardContent>
            </form>
        </UiCard>
    )
}

export default OfferOfferNote
